import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react';
import React from 'react';
import { Navigate, RouteObject, redirect } from 'react-router-dom';
import DashboardLayout from './components/dashboard-layout';
import ProtectedRoute from './config/protected-route';
import AccessDenied from './pages/access-denied/access-denied';
import AdminTool from './pages/admin-tool';
import DashboardHome from './pages/dashboard-home';
import EmailSubject from './pages/email-subject';
import ErrorPage from './pages/error/error';
import GoogleChatbot from './pages/google-chatbot';
import Login from './pages/login';
import SalesScript from './pages/sales-script';
import UserFiles from './pages/user-files';
import WhereIsMyTech from './pages/where-is-my-tech';

import OpportunityAnalytics, { OpportunityAnalyticsProvider } from './components/oppurtunity-anlytics';
import Analysis from './components/oppurtunity-anlytics/analysis';
import ClusterManager from './components/oppurtunity-anlytics/ClusterManager';
import CompareCluster from './components/oppurtunity-anlytics/CompareCluster';
import Dashboard from './components/oppurtunity-anlytics/dashboard';
import SubmittedRequests from './components/oppurtunity-anlytics/submittedRequests';
import ViewRequestData from './components/oppurtunity-anlytics/viewRequestData';
import AgentAssistUI from './pages/360-a/agent-assist';
import BrightInsightComp from './pages/bright-insights';
import LLMResponse from './pages/llm-response';
import CallflowScipt from './pages/callflow-script';

interface AuthenticatedRedirectProps {
	children: React.ReactNode;
}

const AuthenticatedRedirect: React.FC<AuthenticatedRedirectProps> = ({ children }) => {
	const isAuthenticated = useIsAuthenticated();
	if (isAuthenticated) {
		return <Navigate to="/" />;
	}
	return <>{children}</>;
};

const routes: RouteObject[] = [
	{
		path: '/',
		element: (
			<React.Fragment>
				<AuthenticatedTemplate>
					<DashboardLayout />
				</AuthenticatedTemplate>
				<UnauthenticatedTemplate>
					<Login />
				</UnauthenticatedTemplate>
			</React.Fragment>
		),
		errorElement: <ErrorPage />,
		children: [
			{
				index: true,
				element: <DashboardHome />,
			},
			{
				path: 'user-files',
				element: <ProtectedRoute roles={['User-Employee']} />,
				children: [
					{
						path: '',
						element: <UserFiles />,
					},
				],
			},
			{
				path: 'email-subject',
				element: <ProtectedRoute roles={['User-Marketing', 'User-Employee']} />,
				children: [
					{
						path: '',
						element: <EmailSubject />,
					},
				],
			},
			{
				path: 'static-agent-assist',
				element: <ProtectedRoute roles={['User-Agent-Assist', 'User-Employee']} />,
				children: [
					{
						path: '',
						element: <AgentAssistUI />,
					},
				],
			},
			{
				path: 'where-is-my-tech',
				element: <ProtectedRoute roles={['User-Employee']} />,
				children: [
					{
						path: '',
						element: <WhereIsMyTech />,
					},
				],
			},
			{
				path: 'admin-tool',
				element: <ProtectedRoute roles={['Administrator', 'User-Agent-Assist', 'User-Employee', 'User-Marketing']} />,
				children: [
					{
						path: '',
						element: <AdminTool />,
					},
				],
			},
			{
				path: 'llm-response',
				element: <ProtectedRoute roles={['Administrator', 'User-Agent-Assist', 'User-Employee', 'User-Marketing']} />,
				children: [
					{
						path: '',
						element: <LLMResponse />,
					},
				],
			},
			{
				path: 'bright-insights',
				element: <ProtectedRoute roles={['User-Agent-Assist', 'User-Marketing', 'User-Employee']} />,
				children: [
					{
						path: '',
						element: <BrightInsightComp />,
					},
				],
			},
			{
				path: 'chatbot',
				element: <ProtectedRoute roles={['User-Conversational-AI', 'User-Employee']} />,
				children: [
					{
						path: '',
						element: <GoogleChatbot />,
					},
				],
			},
			{
				path: 'callflow-script',
				element: <ProtectedRoute roles={['User-Conversational-AI', 'User-Employee']} />,
				children: [
					{
						path: '',
						element: <CallflowScipt />,
					},
				],
			},
		],
	},
	{
		path: 'sales-script',
		element: <ProtectedRoute roles={['User-Marketing', 'User-Employee']} />,
		children: [
			{
				path: '',
				element: <SalesScript />,
			},
		],
	},
	{
		path: 'opportunity-analytics',
		element: <OpportunityAnalytics />,
		children: [
			{
				index: true,
				element: <Dashboard />,
			},
			{
				path: 'analysis-request',
				element: (
					<OpportunityAnalyticsProvider>
						<Analysis />
					</OpportunityAnalyticsProvider>
				),
			},
			{
				path: 'submitted-requests',
				element: (
					<OpportunityAnalyticsProvider>
						<SubmittedRequests />
					</OpportunityAnalyticsProvider>
				),
			},
			{
				path: 'cluster-manager',
				element: (
					<OpportunityAnalyticsProvider>
						<ClusterManager />
					</OpportunityAnalyticsProvider>
				),
			},
			{
				path: 'compare',
				element: (
					<OpportunityAnalyticsProvider>
						<CompareCluster />
					</OpportunityAnalyticsProvider>
				),
			},
		],
	},
	{
		path: 'view-request/:id',
		element: <ProtectedRoute roles={['Administrator', 'User-Employee', 'User-Marketing']} />,
		children: [
			{
				path: '',
				element: <ViewRequestData />,
			},
		],
	},
	{
		path: 'logout',
		async action() {
			return redirect('/login');
		},
	},
	{
		path: 'login',
		element: (
			<AuthenticatedRedirect>
				<Login />
			</AuthenticatedRedirect>
		),
	},
	{
		path: 'error',
		element: <ErrorPage />,
	},
	{
		path: 'access-denied',
		element: <AccessDenied />,
	},
	{
		path: '*',
		element: <ErrorPage />,
	},
];

export default routes;
